import {
  AiLeasingAgentVisibility,
  AiLeasingAgentVisibilityState
} from "types/landlord/address";
import { AGENTS_TAB_URL, getLeasingAgentRoute } from "shared/utils/agentRoutes";

const PHONE_NUMBERS_URL = "/phone-numbers";

export const STATE_LABELS: Record<AiLeasingAgentVisibilityState, string> = {
  visible: "Can see this unit",
  not_connected: "Not connected",
  no_access: "No access",
  not_listed: "Not listed",
  locked: "BoomCRM Pro"
};

/** Where the popover's action takes the user. */
type PopoverAction =
  | { kind: "navigate"; label: string; to: string; settings: boolean }
  | { kind: "list_unit"; label: string };

interface PopoverCopy {
  title: string;
  body: string;
  action: PopoverAction;
}

/**
 * "Not connected" sends the user to the agent when there is nothing on the
 * Phone numbers screen that could fix it: no agent, an agent switched off, or
 * an agent that doesn't answer any number yet.
 */
const needsAgentSetup = (agent: AiLeasingAgentVisibility["agent"]) =>
  !agent || !agent.active || agent.phone_numbers_count === 0;

const agentRoute = (agent: AiLeasingAgentVisibility["agent"]) =>
  agent ? getLeasingAgentRoute(agent) : AGENTS_TAB_URL;

export const getPopoverCopy = (
  visibility: AiLeasingAgentVisibility,
  propertyName: string,
  { unitListed }: { unitListed: boolean }
): PopoverCopy | null => {
  switch (visibility.state) {
    case "visible":
      return {
        title: "The AI leasing agent can see this unit",
        body: `${propertyName} is linked to a phone number the AI leasing agent answers. It can tell callers and texters about this unit and help them schedule a showing.`,
        action: {
          kind: "navigate",
          label:
            visibility.phone_numbers.length > 1
              ? "View phone numbers"
              : "View phone number",
          to: PHONE_NUMBERS_URL,
          settings: false
        }
      };
    case "not_connected":
      return {
        title: "The AI leasing agent can't see this unit",
        body: `${propertyName} isn't linked to any phone number the AI leasing agent answers, so callers and texters won't hear about this unit.`,
        action: needsAgentSetup(visibility.agent)
          ? {
              kind: "navigate",
              label: "Set up AI leasing agent",
              to: agentRoute(visibility.agent),
              settings: true
            }
          : {
              kind: "navigate",
              label: "Link a phone number",
              to: PHONE_NUMBERS_URL,
              settings: false
            }
      };
    case "no_access":
      return {
        title: "The AI leasing agent can't access this property",
        body: `The agent's team account doesn't have access to ${propertyName}, so it can't talk about this unit even though the property is linked to its number.`,
        action: {
          kind: "navigate",
          label: "Go to AI agent settings",
          to: agentRoute(visibility.agent),
          settings: true
        }
      };
    case "not_listed":
      // List unit is on but the listing hasn't been published for the agent
      // yet (listing sync hasn't caught up), so "turn it on" would be wrong.
      if (unitListed)
        return {
          title: "This unit's listing isn't live yet",
          body: `List unit is on, but the listing hasn't been published for the AI leasing agent yet, so callers and texters won't hear about this unit. Turn List unit off and on again to republish it, or contact support if it stays this way.`,
          action: { kind: "list_unit", label: "Go to List unit" }
        };
      return {
        title: "This unit isn't listed",
        body: `${propertyName} is linked to a number the AI leasing agent answers, but the agent only talks about listed units. Turn on List unit to let it see this unit.`,
        action: { kind: "list_unit", label: "Go to List unit" }
      };
    case "locked":
      return null;
  }
};
